import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  MenuItem,
  Box,
  useMediaQuery,
  useTheme,
  Card,
  CardHeader,
  CardContent,
  CardActions,
} from "@mui/material";
import {
  initialTrackerData,
  PatientData,
  TrackerData,
} from "../../types/Patient";
import { ListData } from "../../types/List";
import { ConsultingRoomData } from "../../types/ConsultingRoom";
import { getData } from "../../api/ConsultingRoomApi";

interface Props {
  onSubmit: (data: Partial<TrackerData>) => void;
  onClose: () => void;
  workflowData: ListData[];
}

const RegistrationForm: React.FC<Props> = ({
  onSubmit,
  onClose,
  workflowData,
}) => {
  const [inputData, setInputData] =
    useState<Partial<TrackerData>>(initialTrackerData);
  const [consultingRooms, setConsultingRooms] = useState<
    Partial<ConsultingRoomData[]>
  >([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    setInputData((prev: Partial<PatientData>) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    if (!inputData.workflowId)
      newErrors.workflowId = "The workflow is required";

    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    onSubmit(inputData);
  };

  const loadData = async () => {
    const result = await getData();
    setConsultingRooms(result);
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <Box display="flex" justifyContent="center" width="100%" marginTop={10}>
      <Card
        sx={{
          width: isMobile ? "100%" : "35%",
          paddingY: 1,
        }}
      >
        <CardHeader
          title="Register Patients"
          titleTypographyProps={{
            fontSize: 20,
            fontWeight: "bold",
          }}
          sx={{ textAlign: "center" }}
        />

        <form onSubmit={handleSubmit}>
          <CardContent>
            <Box display="grid" gridTemplateColumns="1fr" gap={2}>
              <TextField
                select
                name="workflowId"
                label="Workflow"
                value={inputData.workflowId}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.workflowId}
                helperText={errors.workflowId}
              >
                <MenuItem key={0} value={0}>
                  Please select
                </MenuItem>
                {workflowData.map((data) => (
                  <MenuItem key={data.id} value={data.id}>
                    {data.name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                name="consultingRoomId"
                label="Consulting Room"
                value={inputData.consultingRoomId}
                onChange={handleChange}
                fullWidth
                size="small"
              >
                <MenuItem key={0} value={0}>
                  Please select
                </MenuItem>
                {consultingRooms.map((data) => (
                  <MenuItem key={data?.id} value={data?.id}>
                    {data?.name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                name="insuranceCompanyId"
                label="Insurance Company"
                value={inputData.insuranceId}
                onChange={handleChange}
                fullWidth
                size="small"
              >
                <MenuItem key={0} value={0}>
                  Please select
                </MenuItem>
              </TextField>

              <TextField
                name="note"
                label="Note"
                value={inputData.note}
                onChange={handleChange}
                fullWidth
                multiline
                rows={6}
                size="small"
              />
            </Box>
          </CardContent>

          <CardActions
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 2,
              paddingX: 2,
              paddingBottom: 2,
            }}
          >
            <Button
              variant="outlined"
              color="secondary"
              onClick={onClose}
              fullWidth
            >
              Close
            </Button>

            <Button type="submit" variant="contained" color="success" fullWidth>
              Save
            </Button>
          </CardActions>
        </form>
      </Card>
    </Box>
  );
};

export default RegistrationForm;
